import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hopsy_cart") || "[]");
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("hopsy_cart", JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, size, qty = 1) => {
    setItems((prev) => {
      const key = `${product.slug}__${size.label}`;
      const idx = prev.findIndex((p) => `${p.product_slug}__${p.size_label}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        product_slug: product.slug,
        product_name: product.name,
        product_image: product.image,
        size_label: size.label,
        unit_price: size.price,
        quantity: qty,
      }];
    });
  }, []);

  const remove = useCallback((product_slug, size_label) => {
    setItems((prev) => prev.filter((p) => !(p.product_slug === product_slug && p.size_label === size_label)));
  }, []);

  const setQty = useCallback((product_slug, size_label, qty) => {
    setItems((prev) => prev.map((p) =>
      p.product_slug === product_slug && p.size_label === size_label
        ? { ...p, quantity: Math.max(1, Math.min(50, qty)) }
        : p
    ));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const replaceItems = useCallback((newItems) => {
    if (!Array.isArray(newItems)) return;
    setItems(newItems.map((p) => ({
      product_slug: p.product_slug,
      product_name: p.product_name,
      product_image: p.product_image,
      size_label: p.size_label,
      unit_price: Number(p.unit_price) || 0,
      quantity: Math.max(1, Math.min(50, Number(p.quantity) || 1)),
    })));
  }, []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, p) => s + p.unit_price * p.quantity, 0);
    const count = items.reduce((s, p) => s + p.quantity, 0);
    return { subtotal, count };
  }, [items]);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, replaceItems, ...totals }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
